import React, { useState, type FC } from "react";
import clsx from "clsx";
import Text from "../../../../../shared-components/css-modules/lexus/atoms/Text";
import { useCarConfigSelector } from "../../../../redux/store";
import styles from "./Summary.module.scss";
import a11y from "../../../../../shared-components/css-modules/lexus/utils/Accessibility.module.scss";
import { useCarConfigLabel } from "../../../../utils/labelUtils";
import { useTabsContext } from "./SummaryTabsContext";
import Button from "../../../../../shared-components/css-modules/lexus/atoms/Button";
import Icon from "../../../../../shared-components/css-modules/lexus/atoms/Icon";

const SummaryTyCode: FC = () => {
    const tabsContext = useTabsContext();
    const qrCode = useCarConfigSelector(({ save }) => save.qrCode);
    const configuration = useCarConfigSelector(({ save }) => save.data);
    const [copiedCode, setCopiedCode] = useState<boolean>(false);
    const [copiedLink, setCopiedLink] = useState<boolean>(false);
    const [
        carConfigSummaryTyCodeTitle,
        carConfigSummaryTyCodeCopied,
        carConfigSummaryTyCodeCopyCode,
        carConfigSummaryTyCodeCopyLink,
        carConfigSummaryTyCodeLinkTitle,
        carConfigSummaryTyCodeDescription,
        carConfigSummaryTyCodeScanDescription,
        carConfigSummaryTyCodeYourConfiguration,
    ] = useCarConfigLabel([
        "carConfigSummaryTyCodeTitle",
        "carConfigSummaryTyCodeCopied",
        "carConfigSummaryTyCodeCopyCode",
        "carConfigSummaryTyCodeCopyLink",
        "carConfigSummaryTyCodeLinkTitle",
        "carConfigSummaryTyCodeDescription",
        "carConfigSummaryTyCodeScanDescription",
        "carConfigSummaryTyCodeYourConfiguration",
    ]);

    if (!configuration || ["business", "overview"].includes(tabsContext.openTab || "")) return null;

    const handleCopy = (copyObject: "code" | "link", textToCopy: string): void => {
        navigator.clipboard.writeText(textToCopy);

        if (copyObject === "code") setCopiedCode(true);
        else setCopiedLink(true);

        setTimeout(() => {
            if (copyObject === "code") setCopiedCode(false);
            else setCopiedLink(false);
        }, 3000);
    };

    return (
        <div className={styles["tycode-card"]}>
            <div className={styles["tycode-heading"]}>
                <Text tag="h3" variant="apheleia-h5" className={styles["tycode-title"]}>
                    {carConfigSummaryTyCodeTitle}
                </Text>
                <Text tag="p" variant="apheleia-body">
                    {carConfigSummaryTyCodeDescription}
                </Text>
            </div>

            <div className={styles["tycode-sub-card"]}>
                <div>
                    <Text tag="h4" variant="apheleia-body">
                        <strong>{carConfigSummaryTyCodeYourConfiguration}</strong>
                    </Text>
                    <Text variant="apheleia-body">
                        {configuration.Personal?.Code ?? configuration.ConfigurationCode}
                    </Text>
                </div>
                <Button
                    variant="btn-secondary"
                    sizing="pill"
                    onClick={() => handleCopy("code", configuration.Personal?.Code ?? configuration.ConfigurationCode)}
                    className={clsx(styles.copyBtn, copiedCode && styles.codeCopied)}
                >
                    <Icon variant={copiedCode ? "check" : "copy"} sizing="sm" />
                    <span className={styles.pillText}>
                        <span className={clsx(!copiedCode && styles.hidePillText)}>{carConfigSummaryTyCodeCopied}</span>
                        <span className={clsx(copiedCode && styles.hidePillText)}>
                            {carConfigSummaryTyCodeCopyCode}
                        </span>
                    </span>
                </Button>
            </div>

            <div className={styles["tycode-sub-card"]}>
                <div>
                    <Text tag="div" variant="apheleia-body">
                        <strong>{carConfigSummaryTyCodeLinkTitle}</strong>
                    </Text>
                    <Text tag="span" variant="apheleia-body">
                        <span className={a11y["sr-only"]}>{`${carConfigSummaryTyCodeTitle}:`}</span>
                        tme.one/{configuration.Personal?.Code ?? configuration.ConfigurationCode}
                    </Text>
                </div>

                <Button
                    variant="btn-secondary"
                    sizing="pill"
                    onClick={() => handleCopy("link", configuration.Personal?.Link ?? configuration.ConfigurationLink)}
                    className={clsx(styles.copyBtn, copiedLink && styles.codeCopied)}
                >
                    <Icon variant={copiedLink ? "check" : "copy"} sizing="sm" />
                    <span className={styles.pillText}>
                        <span className={clsx(!copiedLink && styles.hidePillText)}>{carConfigSummaryTyCodeCopied}</span>
                        <span className={clsx(copiedLink && styles.hidePillText)}>
                            {carConfigSummaryTyCodeCopyLink}
                        </span>
                    </span>
                </Button>
            </div>

            <div className={styles["tycode-scan-code"]}>
                <Text tag="p" variant="apheleia-body">
                    {carConfigSummaryTyCodeScanDescription}
                </Text>

                <img src={qrCode} alt={carConfigSummaryTyCodeTitle} />
            </div>
        </div>
    );
};

export default SummaryTyCode;
